import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { StatusBadge } from '../components/shared/StatusBadge';
import { EmptyState } from '../components/shared/EmptyState';
import { evidenceAPI, controlsAPI } from '../lib/api';
import { formatDate, truncate } from '../lib/utils';
import { FileCheck, Plus, Trash2, ExternalLink, Search, Filter, Upload, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';

export default function EvidencePage() {
  const [evidence, setEvidence] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [uploadMode, setUploadMode] = useState('manual');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    source_type: 'manual',
    file_url: '',
    control_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [evidenceRes, controlsRes] = await Promise.all([
        evidenceAPI.getAll(),
        controlsAPI.getAll(),
      ]);
      setEvidence(evidenceRes.data);
      setControls(controlsRes.data);
    } catch (error) {
      toast.error('Failed to load evidence');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!formData.title) {
        setFormData(prev => ({ ...prev, title: file.name.split('.')[0] }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (uploadMode === 'upload' && selectedFile) {
        // File upload
        const formDataObj = new FormData();
        formDataObj.append('title', formData.title);
        formDataObj.append('description', formData.description || '');
        formDataObj.append('source_type', 'document');
        formDataObj.append('control_ids', formData.control_ids.join(','));
        formDataObj.append('file', selectedFile);

        await api.post('/evidence/upload', formDataObj, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Manual entry
        await evidenceAPI.create(formData);
      }
      
      toast.success('Evidence added successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add evidence');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      source_type: 'manual',
      file_url: '',
      control_ids: [],
    });
    setSelectedFile(null);
    setUploadMode('manual');
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await evidenceAPI.updateStatus(id, status);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this evidence?')) return;
    
    try {
      await evidenceAPI.delete(id);
      toast.success('Evidence deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete evidence');
    }
  };

  const toggleControlSelection = (controlId) => {
    setFormData(prev => ({
      ...prev,
      control_ids: prev.control_ids.includes(controlId)
        ? prev.control_ids.filter(id => id !== controlId)
        : [...prev.control_ids, controlId]
    }));
  };

  const filteredEvidence = evidence.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Stats
  const stats = {
    total: evidence.length,
    compliant: evidence.filter(e => e.status === 'compliant').length,
    pending: evidence.filter(e => e.status === 'pending').length,
    gap: evidence.filter(e => e.status === 'gap').length,
  };

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader 
        title="Evidence Lab" 
        description="Collect and manage compliance evidence"
        actions={
          <Button
            data-testid="add-evidence-btn"
            onClick={() => setIsDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            <Plus size={18} className="mr-2" />
            Add Evidence
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider">Total</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
            <p className="text-sm font-mono text-emerald-600 uppercase tracking-wider">Compliant</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{stats.compliant}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-mono text-slate-500 uppercase tracking-wider">Pending</p>
            <p className="text-2xl font-bold text-slate-600 mt-1">{stats.pending}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-sm font-mono text-red-600 uppercase tracking-wider">Gap</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{stats.gap}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="evidence-search"
              placeholder="Search evidence..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 rounded-sm"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="evidence-filter" className="w-40 rounded-sm">
              <Filter size={16} className="mr-2 text-slate-400" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="compliant">Compliant</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="gap">Gap</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Evidence Table */}
        {filteredEvidence.length > 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Source</th>
                    <th>Controls</th>
                    <th>Status</th>
                    <th>Collected</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvidence.map((item) => (
                    <tr key={item.id} className="table-row-hover">
                      <td>
                        <div className="flex items-center gap-2">
                          {item.file_name && <File size={16} className="text-slate-400" />}
                          <div>
                            <p className="font-medium text-slate-900">{item.title}</p>
                            {item.description && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                {truncate(item.description, 60)}
                              </p>
                            )}
                            {item.file_name && (
                              <p className="text-xs text-blue-500 mt-0.5">{item.file_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-neutral">
                          {item.source_type}
                        </span>
                      </td>
                      <td>
                        <span className="text-slate-600">
                          {item.control_ids?.length || 0} linked
                        </span>
                      </td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="cursor-pointer">
                              <StatusBadge status={item.status} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => handleStatusUpdate(item.id, 'compliant')}>
                              Mark Compliant
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusUpdate(item.id, 'pending')}>
                              Mark Pending
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusUpdate(item.id, 'gap')}>
                              Mark as Gap
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      <td className="text-slate-500 text-sm">
                        {formatDate(item.collected_at)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {item.file_url && !item.file_url.startsWith('stored://') && (
                            <a
                              href={item.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <ExternalLink size={16} />
                            </a>
                          )}
                          <button
                            data-testid={`delete-evidence-${item.id}`}
                            onClick={() => handleDelete(item.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={FileCheck}
            title="No evidence collected"
            description="Start by adding evidence to demonstrate compliance with your controls."
            action={
              <Button
                data-testid="empty-add-evidence-btn"
                onClick={() => setIsDialogOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                <Plus size={18} className="mr-2" />
                Add Your First Evidence
              </Button>
            }
          />
        )}
      </div>

      {/* Add Evidence Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary">Add Evidence</DialogTitle>
          </DialogHeader>
          
          <Tabs value={uploadMode} onValueChange={setUploadMode} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="manual" data-testid="tab-manual">Manual Entry</TabsTrigger>
              <TabsTrigger value="upload" data-testid="tab-upload">File Upload</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <TabsContent value="upload" className="space-y-4 mt-0">
                <div 
                  className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
                    selectedFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-blue-400'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
                  />
                  {selectedFile ? (
                    <div>
                      <File size={32} className="mx-auto text-emerald-600 mb-2" />
                      <p className="font-medium text-emerald-700">{selectedFile.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={32} className="mx-auto text-slate-400 mb-2" />
                      <p className="text-slate-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLS, PNG, JPG up to 10MB</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="manual" className="space-y-4 mt-0">
                <div>
                  <Label>Source Type</Label>
                  <Select 
                    value={formData.source_type} 
                    onValueChange={(value) => setFormData({ ...formData, source_type: value })}
                  >
                    <SelectTrigger data-testid="evidence-source-select" className="mt-1.5 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Upload</SelectItem>
                      <SelectItem value="integration">Integration</SelectItem>
                      <SelectItem value="screenshot">Screenshot</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>File URL (optional)</Label>
                  <Input
                    data-testid="evidence-url-input"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://..."
                    type="url"
                    className="mt-1.5 rounded-sm"
                  />
                </div>
              </TabsContent>

              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="evidence-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., MFA Configuration Screenshot"
                  required
                  className="mt-1.5 rounded-sm"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  data-testid="evidence-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this evidence..."
                  rows={3}
                  className="mt-1.5 rounded-sm"
                />
              </div>

              {controls.length > 0 && (
                <div>
                  <Label>Link to Controls</Label>
                  <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-2">
                    {controls.slice(0, 20).map((control) => (
                      <div key={control.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`control-${control.id}`}
                          checked={formData.control_ids.includes(control.id)}
                          onCheckedChange={() => toggleControlSelection(control.id)}
                        />
                        <label 
                          htmlFor={`control-${control.id}`}
                          className="text-sm text-slate-700 cursor-pointer"
                        >
                          {control.control_code} - {truncate(control.title, 40)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setIsDialogOpen(false); resetForm(); }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-evidence-btn"
                  disabled={submitting || (uploadMode === 'upload' && !selectedFile)}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  {submitting ? 'Adding...' : 'Add Evidence'}
                </Button>
              </div>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
